// Geometry for the occlusal (top-down) arch chart.
//
// Teeth sit along two half-ellipses: the upper arch opens downward from the
// top of the drawing, the lower arch opens upward from the bottom. Each tooth
// is placed by arc length using real average crown sizes, so incisors are
// narrow and molars wide, and is rotated to follow the curve.

import { isPrimary, isUpper, positionOf } from '@/utils/dental';

export const ARCH_W = 640;
export const ARCH_H = 720;
const CX = ARCH_W / 2;
const EDGE = 52; // incisor centre distance from the top / bottom edge
const A = 172; // half-width of the arch
const B = 200; // depth of the arch
const THETA_END = 1.95; // where the last permanent molar ends (past 90°: molars turn inward)
const GAP_MM = 0.6;
const PRIMARY_ZOOM = 1.25;

export type ToothKind = 'incisor' | 'canine' | 'premolar' | 'molar';

/** Mesiodistal width × buccolingual depth in mm, by position 1..8. */
const SIZES = {
  upper: [
    [8.5, 7],
    [6.5, 6],
    [7.5, 8],
    [7, 9],
    [6.5, 9],
    [10, 11],
    [9, 11],
    [8.5, 10.5],
  ],
  lower: [
    [5.5, 6],
    [6, 6.2],
    [7, 7.5],
    [7, 8],
    [7, 8.5],
    [11, 10.5],
    [10.5, 10],
    [10, 9.5],
  ],
  upperPrimary: [
    [6.5, 5],
    [5, 4.5],
    [7, 7],
    [7, 8.5],
    [9, 10],
  ],
  lowerPrimary: [
    [4, 3.8],
    [4.5, 4],
    [5, 5.5],
    [7.5, 7],
    [9.5, 9],
  ],
} as const;

export type Placement = {
  tooth: number;
  kind: ToothKind;
  /** Centre. */
  x: number;
  y: number;
  /** Unit vector along the arch, pointing away from the midline (distal). */
  tx: number;
  ty: number;
  /** Unit vector pointing out of the arch (buccal / labial). */
  nx: number;
  ny: number;
  /** Width along the arch and depth across it, in drawing units. */
  w: number;
  d: number;
};

export function kindOf(tooth: number): ToothKind {
  const p = positionOf(tooth);
  if (p <= 2) return 'incisor';
  if (p === 3) return 'canine';
  if (isPrimary(tooth) || p >= 6) return 'molar';
  return 'premolar';
}

function sizesFor(tooth: number): readonly (readonly [number, number])[] {
  const upper = isUpper(tooth);
  if (isPrimary(tooth)) return upper ? SIZES.upperPrimary : SIZES.lowerPrimary;
  return upper ? SIZES.upper : SIZES.lower;
}

const curvePoint = (theta: number) => ({ x: A * Math.sin(theta), y: B * (1 - Math.cos(theta)) });

// Arc-length table for the half-ellipse, from the midline outward.
const STEPS = 800;
const TABLE: { theta: number; len: number }[] = (() => {
  const out = [{ theta: 0, len: 0 }];
  let prev = curvePoint(0);
  for (let i = 1; i <= STEPS; i += 1) {
    const theta = (i / STEPS) * 3;
    const p = curvePoint(theta);
    out.push({ theta, len: out[i - 1].len + Math.hypot(p.x - prev.x, p.y - prev.y) });
    prev = p;
  }
  return out;
})();

function thetaAt(len: number): number {
  const i = TABLE.findIndex((row) => row.len >= len);
  if (i <= 0) return i === 0 ? 0 : TABLE[TABLE.length - 1].theta;
  const a = TABLE[i - 1];
  const b = TABLE[i];
  return a.theta + ((len - a.len) / (b.len - a.len)) * (b.theta - a.theta);
}

const lengthAt = (theta: number) => TABLE[Math.round((theta / 3) * STEPS)].len;

/** Pixels per millimetre for the permanent arch, so 8 teeth fill the curve. */
function permanentScale(upper: boolean): number {
  const sizes = upper ? SIZES.upper : SIZES.lower;
  const totalMm = sizes.reduce((sum, [w]) => sum + w + GAP_MM, 0);
  return lengthAt(THETA_END) / totalMm;
}

/** Quadrant teeth ordered from the midline outward (11, 12, … 18). */
function quadrantTeeth(quadrant: number): number[] {
  const count = quadrant >= 5 ? 5 : 8;
  return Array.from({ length: count }, (_, i) => quadrant * 10 + i + 1);
}

function placeQuadrant(quadrant: number): Placement[] {
  const teeth = quadrantTeeth(quadrant);
  const upper = isUpper(teeth[0]);
  // Viewer's left is the patient's right: quadrants 1, 4, 5, 8.
  const side = [1, 4, 5, 8].includes(quadrant) ? -1 : 1;
  const vertical = upper ? 1 : -1;
  const originY = upper ? EDGE : ARCH_H - EDGE;
  const scale = permanentScale(upper) * (isPrimary(teeth[0]) ? PRIMARY_ZOOM : 1);
  const sizes = sizesFor(teeth[0]);

  let along = GAP_MM / 2;
  return teeth.map((tooth, i) => {
    const [wMm, dMm] = sizes[i];
    const theta = thetaAt((along + wMm / 2) * scale);
    along += wMm + GAP_MM;
    const p = curvePoint(theta);
    const x = CX + side * p.x;
    const y = originY + vertical * p.y;
    // Tangent: derivative of the curve, pointing distal.
    const rawTx = side * A * Math.cos(theta);
    const rawTy = vertical * B * Math.sin(theta);
    const len = Math.hypot(rawTx, rawTy);
    const tx = rawTx / len;
    const ty = rawTy / len;
    // Normal: perpendicular to the tangent, pointing away from the arch centre.
    const centreY = originY + vertical * B;
    const flip = -ty * (x - CX) + tx * (y - centreY) < 0 ? -1 : 1;
    return {
      tooth,
      kind: kindOf(tooth),
      x,
      y,
      tx,
      ty,
      nx: -ty * flip,
      ny: tx * flip,
      w: wMm * scale,
      d: dMm * scale,
    };
  });
}

/** Placements for one dentition, in chart reading order per arch. */
export function archLayout(primary: boolean): { upper: Placement[]; lower: Placement[] } {
  const [ur, ul, ll, lr] = primary ? [5, 6, 7, 8] : [1, 2, 3, 4];
  return {
    upper: [...placeQuadrant(ur).reverse(), ...placeQuadrant(ul)],
    lower: [...placeQuadrant(lr).reverse(), ...placeQuadrant(ll)],
  };
}
